import { useMemo } from 'react';
import type { ItemV2 } from '@/common/api/types/items';
import type { ISwimlaneHeader } from '@/common/api/types/loopVisualization';
import { getAllItemFieldValues } from '@/features/visualizations/utils/fieldExtraction';

export function useUniqueHeaderValues(
  items: ItemV2[],
  currentColorByField: string,
  isBaseColorSelected: boolean,
  matchingHeader: ISwimlaneHeader | null,
  headerForColorField: ISwimlaneHeader | null = null,
  showEmptyHeaders?: boolean,
): string[] {
  return useMemo(() => {
    if (!currentColorByField || isBaseColorSelected) {
      return [];
    }

    const values: string[] = [];
    const addedValues = new Set<string>();

    if (matchingHeader && 'options' in matchingHeader) {
      const options = matchingHeader.options as Array<{ id: number | string }>;
      const optionIds = new Set(options.map((o) => o.id.toString()));

      // Return the full option domain so color indices stay stable when toggling showEmptyHeaders.
      for (const option of options) {
        const optionId = option.id.toString();
        if (!addedValues.has(optionId)) {
          values.push(optionId);
          addedValues.add(optionId);
        }
      }

      // Append 'null' when items have missing/orphaned values, or when showEmptyHeaders forces the None group visible.
      let hasOrphanOrMissing = false;
      for (const item of items) {
        const fieldValues = getAllItemFieldValues(item, currentColorByField);
        if (fieldValues.length === 0) {
          hasOrphanOrMissing = true;
          break;
        }
        for (const fieldValue of fieldValues) {
          if (!optionIds.has(fieldValue)) {
            hasOrphanOrMissing = true;
            break;
          }
        }
        if (hasOrphanOrMissing) break;
      }

      if (
        (hasOrphanOrMissing || showEmptyHeaders === true) &&
        (matchingHeader.type === 'CustomPropertySelectorByID' ||
          matchingHeader.type === 'ParentItemSelector') &&
        !addedValues.has('null')
      ) {
        values.push('null');
      }

      return values;
    }

    if (headerForColorField && 'options' in headerForColorField) {
      const optionIds = new Set(
        (headerForColorField.options as Array<{ id: number | string }>).map((option) =>
          option.id.toString(),
        ),
      );

      let hasOrphanOrMissing = false;

      items.forEach((item) => {
        const fieldValues = getAllItemFieldValues(item, currentColorByField);
        if (fieldValues.length === 0) {
          hasOrphanOrMissing = true;
          return;
        }

        for (const fieldValue of fieldValues) {
          if (!optionIds.has(fieldValue)) {
            hasOrphanOrMissing = true;
            continue;
          }

          if (!addedValues.has(fieldValue)) {
            values.push(fieldValue);
            addedValues.add(fieldValue);
          }
        }
      });

      if (hasOrphanOrMissing && !addedValues.has('null')) {
        values.push('null');
      }

      return values;
    }

    items.forEach((item) => {
      for (const fieldValue of getAllItemFieldValues(item, currentColorByField)) {
        if (!addedValues.has(fieldValue)) {
          values.push(fieldValue);
          addedValues.add(fieldValue);
        }
      }
    });

    return values.sort();
  }, [
    items,
    currentColorByField,
    isBaseColorSelected,
    matchingHeader,
    headerForColorField,
    showEmptyHeaders,
  ]);
}
